import { ReactElement, useMemo, useState } from 'react'
// @ts-ignore
import currencyFormatter from 'currency-formatter'
import Calendar from 'react-calendar'
import 'react-calendar/dist/Calendar.css'
import { useParams, useMatches } from 'react-router-dom'
// @ts-ignore
import Loader from '../../components/Loader'
import { Breadcrumb } from '../../components/Breadcrumb'
import { useGuard } from '../../hooks/auth'
import { useEvent } from '../../hooks/events'
import {
  useReceivablesCalendar,
  useReceivablesDayDetail,
} from '../../hooks/receivables'
import { ReceivableDay } from '../../types/receivables'
import { truncateString } from '../../utils/string-modeling'
import {
  Badge,
  CalendarWrap,
  CardLabel,
  CardValue,
  CheckboxLabel,
  Container,
  InfoHint,
  DetailPanel,
  DetailTitle,
  Divider,
  EmptyState,
  PageSubtitle,
  PageTitle,
  Select,
  SummaryCard,
  SummaryRow,
  Table,
  TileAdvance,
  TileQtd,
  TileValue,
  Toolbar,
  ToggleButton,
  ToggleGroup,
} from './styles'

const PAYMENT_OPTIONS = [
  { value: '', label: 'Todas as formas' },
  { value: 'PIX', label: 'PIX' },
  { value: 'CREDITO', label: 'Cartão de Crédito' },
  { value: 'NUPAY', label: 'NuPay' },
  { value: 'APPLE_PAY', label: 'Apple Pay' },
  { value: 'GOOGLE_PAY', label: 'Google Pay' },
  { value: 'DEBITO', label: 'Débito' },
  { value: 'DINHEIRO', label: 'Dinheiro (PDV Próprio)' },
]

const pad = (n: number) => String(n).padStart(2, '0')
const toYMD = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const fmtBRL = (v: number) => currencyFormatter.format(v, { code: 'BRL' })
const fmtBR = (ymd: string) => {
  const [y, m, d] = ymd.split('-')
  return `${d}/${m}/${y}`
}

export function ReceiptsCalendar(): ReactElement {
  window.document.title = 'Calendário de Recebimentos'
  const { id } = useParams<{ id: string }>()
  const eventId = Number(id)
  useGuard(['DP17'])

  const { event } = useEvent(eventId)
  const [view, setView] = useState<'calendar' | 'list'>('calendar')
  const [paymentMethod, setPaymentMethod] = useState('')
  const [considerAdvances, setConsiderAdvances] = useState(false)
  const [selectedDay, setSelectedDay] = useState<string | null>(null)

  const advancesActive = considerAdvances && !paymentMethod
  const { calendar, isLoading } = useReceivablesCalendar(eventId, {
    paymentMethod,
    considerAdvances: advancesActive || undefined,
  })
  const { detail, isLoading: isDetailLoading } = useReceivablesDayDetail(
    eventId,
    selectedDay,
    paymentMethod
  )

  const matches = useMatches()
  const crumbs = matches
    .filter((match: any) => Boolean(match.handle?.crumb))
    .map((match: any) => ({ name: match.handle.crumb(match.data), path: match.pathname }))

  const diasMap = useMemo(() => {
    const map = new Map<string, ReceivableDay>()
    calendar?.dias.forEach((d) => map.set(d.data, d))
    return map
  }, [calendar])

  const eventName = truncateString(event?.nome, 40)

  if (isLoading) return <Loader />

  const resumo = calendar?.resumo
  const dias = calendar?.dias ?? []
  const hasData = dias.length > 0

  return (
    <Container>
      <Breadcrumb paths={crumbs} />
      <PageTitle>Calendário de Recebimentos</PageTitle>
      <PageSubtitle>
        {eventName ? `${eventName} — ` : ''}Previsão de recebimentos por data (sem descontar taxas).
      </PageSubtitle>

      <SummaryRow>
        <SummaryCard>
          <CardLabel>Total previsto</CardLabel>
          <CardValue>{fmtBRL(resumo?.totalPrevisto ?? 0)}</CardValue>
        </SummaryCard>
        {advancesActive && (
          <>
            <SummaryCard>
              <CardLabel>
                Antecipado
                <InfoHint title="Principal já antecipado, abatido do fluxo de recebimentos. Pode diferir do valor exibido no Fechamento: lá, a base da linha 'Taxa antecipação de crédito' considera apenas a parte do fluxo que paga taxa (vendas com juros); parcelas antecipadas com taxa 0% entram aqui, mas não naquela base.">
                  ?
                </InfoHint>
              </CardLabel>
              <CardValue>{fmtBRL(resumo?.totalAntecipado ?? 0)}</CardValue>
            </SummaryCard>
            <SummaryCard>
              <CardLabel>
                Já vencido
                <InfoHint title="Parcelas não antecipadas com data prevista já atingida — valor já liquidado, que compõe o saldo disponível para saque.">
                  ?
                </InfoHint>
              </CardLabel>
              <CardValue>{fmtBRL(resumo?.totalVencido ?? 0)}</CardValue>
            </SummaryCard>
            <SummaryCard>
              <CardLabel>
                A vencer
                <InfoHint title="Parcelas não antecipadas com data prevista futura — fluxo que ainda vai liquidar.">
                  ?
                </InfoHint>
              </CardLabel>
              <CardValue>{fmtBRL(resumo?.totalAVencer ?? 0)}</CardValue>
            </SummaryCard>
          </>
        )}
        <SummaryCard>
          <CardLabel>Ingressos</CardLabel>
          <CardValue>{resumo?.totalIngressos ?? 0}</CardValue>
        </SummaryCard>
        <SummaryCard>
          <CardLabel>Pedidos</CardLabel>
          <CardValue>{resumo?.totalPedidos ?? 0}</CardValue>
        </SummaryCard>
        <SummaryCard>
          <CardLabel>Período previsto</CardLabel>
          <CardValue>
            {resumo?.primeiraData ? fmtBR(resumo.primeiraData) : '—'}
            {resumo?.ultimaData ? ` a ${fmtBR(resumo.ultimaData)}` : ''}
          </CardValue>
        </SummaryCard>
      </SummaryRow>

      <Toolbar>
        <ToggleGroup>
          <ToggleButton active={view === 'calendar'} onClick={() => setView('calendar')}>
            Calendário
          </ToggleButton>
          <ToggleButton active={view === 'list'} onClick={() => setView('list')}>
            Lista
          </ToggleButton>
        </ToggleGroup>
        <CheckboxLabel
          disabled={Boolean(paymentMethod)}
          title={
            paymentMethod
              ? 'Disponível apenas com "Todas as formas" — a antecipação consome o fluxo de crédito como um todo.'
              : undefined
          }
        >
          <input
            type="checkbox"
            checked={advancesActive}
            disabled={Boolean(paymentMethod)}
            onChange={(e) => setConsiderAdvances(e.target.checked)}
          />
          Considerar antecipações
        </CheckboxLabel>
        <Select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
          {PAYMENT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </Toolbar>

      {!hasData && <EmptyState>Nenhum recebimento previsto para os filtros selecionados.</EmptyState>}

      {hasData && view === 'calendar' && (
        <CalendarWrap>
          <Calendar
            locale="pt-BR"
            onClickDay={(value: Date) => setSelectedDay(toYMD(value))}
            tileContent={({ date, view: v }: { date: Date; view: string }) => {
              if (v !== 'month') return null
              const info = diasMap.get(toYMD(date))
              if (!info) return null
              const antecipado = advancesActive ? info.valorAntecipado ?? 0 : 0
              return (
                <>
                  <TileValue>
                    {fmtBRL(advancesActive ? info.valorLiquido ?? info.valorPrevisto : info.valorPrevisto)}
                  </TileValue>
                  {antecipado > 0 && <TileAdvance>{fmtBRL(antecipado)}</TileAdvance>}
                  <TileQtd>
                    {info.quantidadeIngressos} ing · {info.quantidadePedidos} ped
                  </TileQtd>
                </>
              )
            }}
          />
        </CalendarWrap>
      )}

      {hasData && view === 'list' && (
        <Table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Valor Previsto</th>
              {advancesActive && <th>Antecipado</th>}
              {advancesActive && <th>A Receber</th>}
              <th>Ingressos</th>
              <th>Pedidos</th>
            </tr>
          </thead>
          <tbody>
            {dias.map((d) => (
              <tr key={d.data} onClick={() => setSelectedDay(d.data)}>
                <td>{fmtBR(d.data)}</td>
                <td className="value">{fmtBRL(d.valorPrevisto)}</td>
                {advancesActive && <td>{fmtBRL(d.valorAntecipado ?? 0)}</td>}
                {advancesActive && <td className="value">{fmtBRL(d.valorLiquido ?? d.valorPrevisto)}</td>}
                <td>{d.quantidadeIngressos}</td>
                <td>{d.quantidadePedidos}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {selectedDay && (
        <DetailPanel>
          <DetailTitle>
            Recebimentos previstos para {fmtBR(selectedDay)}
            {detail
              ? ` — ${fmtBRL(detail.totalPrevisto)} (${detail.totalIngressos} ingresso(s) · ${detail.totalPedidos} pedido(s))`
              : ''}
          </DetailTitle>
          {isDetailLoading && <Loader />}
          {!isDetailLoading && detail && detail.itens.length === 0 && (
            <EmptyState>Sem vendas previstas para esta data.</EmptyState>
          )}
          {!isDetailLoading && detail && detail.itens.length > 0 && (
            <Table>
              <thead>
                <tr>
                  <th>Pedido</th>
                  <th>Evento</th>
                  <th>Data da venda</th>
                  <th>Forma</th>
                  <th>Ingressos</th>
                  <th>Parcela</th>
                  <th>Valor do ticket</th>
                  <th>Recebimento</th>
                </tr>
              </thead>
              <tbody>
                {detail.itens.map((item, idx) => (
                  <tr key={`${item.pedidoId}-${item.parcela}-${idx}`}>
                    <td>{item.pedidoId}</td>
                    <td>{item.eventoNome ?? '—'}</td>
                    <td>{fmtBR(item.dataVenda)}</td>
                    <td>
                      {item.formaPagamento} <Badge>{item.canal}</Badge>
                    </td>
                    <td>{item.quantidadeIngressos}</td>
                    <td>
                      {item.parcela}/{item.totalParcelas}
                    </td>
                    <td className="value">{fmtBRL(item.valorTicket)}</td>
                    <td>{fmtBR(item.dataPrevistaRecebimento)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </DetailPanel>
      )}

      <Divider />
    </Container>
  )
}
